import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { DashboardLayout } from "../components/DashboardLayout";
import { formatApiError } from "../lib/formatApiError";
import { API } from "../lib/constants";

const STATUS_COLORS = {
  draft: "bg-gray-100 text-gray-700",
  scheduled: "bg-blue-100 text-blue-700",
  published: "bg-green-100 text-green-700"
};

export default function SchedulePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [contentType, setContentType] = useState("blog");
  const [scheduledDate, setScheduledDate] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [viewMode, setViewMode] = useState("list");

  const loadItems = useCallback(async () => {
    try {
      const params = filter !== "all" ? `?status=${filter}` : "";
      const { data } = await axios.get(`${API}/schedule/items${params}`);
      setItems(data);
    } catch (err) { console.error("Load schedule items failed:", err); } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const resetForm = () => {
    setTitle("");
    setContent("");
    setContentType("blog");
    setScheduledDate("");
    setNotes("");
    setEditingItem(null);
    setShowForm(false);
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editingItem) {
        await axios.put(`${API}/schedule/items/${editingItem}`, { title, content, scheduled_date: scheduledDate, notes });
      } else {
        await axios.post(`${API}/schedule/items`, { title, content, content_type: contentType, scheduled_date: scheduledDate, notes });
      }
      resetForm();
      loadItems();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  const handleEdit = (item) => {
    setTitle(item.title);
    setContent(item.content);
    setContentType(item.content_type || "blog");
    setScheduledDate(item.scheduled_date);
    setNotes(item.notes || "");
    setEditingItem(item.id);
    setShowForm(true);
  };

  const handleStatusChange = async (id, status) => {
    try {
      await axios.put(`${API}/schedule/items/${id}`, { status });
      loadItems();
    } catch (err) { console.error("Status change failed:", err); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this scheduled item?")) return;
    try {
      await axios.delete(`${API}/schedule/items/${id}`);
      loadItems();
    } catch (err) { console.error("Delete schedule item failed:", err); }
  };

  // Group items by date for calendar view
  const groupedByDate = items.reduce((acc, item) => {
    const date = item.scheduled_date?.split("T")[0] || "unscheduled";
    if (!acc[date]) acc[date] = [];
    acc[date].push(item);
    return acc;
  }, {});

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2">Content Schedule</h1>
            <p className="text-gray-600">Plan and track your content publishing calendar</p>
          </div>
          <button onClick={() => { resetForm(); setShowForm(true); }} className="bg-[#0066FF] text-white px-6 py-3 font-bold hover:bg-[#0052CC] transition-colors rounded-lg" data-testid="new-schedule-btn">
            + Schedule Content
          </button>
        </div>

        {/* Form */}
        {showForm && (
          <div className="bg-white border border-black p-6 rounded-xl mb-8" data-testid="schedule-form">
            <h2 className="text-lg font-bold mb-4">{editingItem ? "Edit Scheduled Content" : "Schedule New Content"}</h2>
            {error && <div className="bg-red-50 border border-red-500 text-red-700 p-3 mb-4 text-sm rounded-lg">{error}</div>}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">Title</label>
                  <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none rounded-lg" required data-testid="schedule-title" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">Scheduled Date</label>
                  <input type="datetime-local" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none rounded-lg" required data-testid="schedule-date" />
                </div>
              </div>
              {!editingItem && (
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">Content Type</label>
                  <select value={contentType} onChange={(e) => setContentType(e.target.value)} className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none rounded-lg" data-testid="schedule-type">
                    <option value="blog">Blog Post</option>
                    <option value="social">Social Media</option>
                    <option value="ad">Advertisement</option>
                    <option value="email">Email</option>
                  </select>
                </div>
              )}
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">Content</label>
                <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={6} className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none resize-none rounded-lg" required data-testid="schedule-content" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">Notes (Optional)</label>
                <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g., Publish to Twitter and LinkedIn" className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none rounded-lg" data-testid="schedule-notes" />
              </div>
              <div className="flex gap-3">
                <button type="submit" className="bg-[#0066FF] text-white px-6 py-2.5 font-bold hover:bg-[#0052CC] transition-colors rounded-lg" data-testid="schedule-submit">
                  {editingItem ? "Update" : "Schedule"}
                </button>
                <button type="button" onClick={resetForm} className="border border-gray-300 px-6 py-2.5 font-bold hover:bg-gray-50 transition-colors rounded-lg" data-testid="schedule-cancel">Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* Filters + View Toggle */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex gap-2">
            {["all", "scheduled", "draft", "published"].map((f) => (
              <button key={f} onClick={() => setFilter(f)} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${filter === f ? "bg-[#0066FF] text-white" : "bg-white border border-gray-200 hover:border-[#0066FF]"}`} data-testid={`filter-${f}`}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            {["list", "calendar"].map((v) => (
              <button key={v} onClick={() => setViewMode(v)} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${viewMode === v ? "bg-black text-white" : "bg-white border border-gray-200"}`} data-testid={`view-${v}`}>
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-black p-12 text-center rounded-xl" data-testid="empty-schedule">
            <p className="text-gray-500 mb-2">No scheduled content yet</p>
            <p className="text-sm text-gray-400">Generate content and schedule it for publishing</p>
          </div>
        ) : viewMode === "list" ? (
          <div className="space-y-4">
            {items.map((item) => (
              <div key={item.id} className="bg-white border border-gray-200 p-5 rounded-xl hover:border-[#0066FF] transition-colors" data-testid={`schedule-item-${item.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-bold truncate">{item.title}</h3>
                      <span className={`px-2.5 py-0.5 text-xs font-bold rounded-full ${STATUS_COLORS[item.status] || STATUS_COLORS.draft}`}>{item.status?.toUpperCase()}</span>
                      <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-500 rounded">{item.content_type}</span>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2 mb-2">{item.content}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-400">
                      <span>Scheduled: {new Date(item.scheduled_date).toLocaleString()}</span>
                      {item.notes && <span>Notes: {item.notes}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {item.status === "scheduled" && (
                      <button onClick={() => handleStatusChange(item.id, "published")} className="text-xs bg-green-100 text-green-700 px-3 py-1.5 font-bold rounded-lg hover:bg-green-200" data-testid={`publish-${item.id}`}>Publish</button>
                    )}
                    {item.status === "draft" && (
                      <button onClick={() => handleStatusChange(item.id, "scheduled")} className="text-xs bg-blue-100 text-blue-700 px-3 py-1.5 font-bold rounded-lg hover:bg-blue-200" data-testid={`schedule-${item.id}`}>Schedule</button>
                    )}
                    <button onClick={() => handleEdit(item)} className="text-xs bg-gray-100 text-gray-600 px-3 py-1.5 font-bold rounded-lg hover:bg-gray-200" data-testid={`edit-${item.id}`}>Edit</button>
                    <button onClick={() => handleDelete(item.id)} className="text-xs bg-red-50 text-red-600 px-3 py-1.5 font-bold rounded-lg hover:bg-red-100" data-testid={`delete-${item.id}`}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Calendar view */
          <div className="space-y-6">
            {Object.entries(groupedByDate).sort(([a], [b]) => a.localeCompare(b)).map(([date, dateItems]) => (
              <div key={date}>
                <h3 className="font-bold text-sm uppercase tracking-wider text-gray-500 mb-3">
                  {date === "unscheduled" ? "Unscheduled" : new Date(date + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
                </h3>
                <div className="space-y-2">
                  {dateItems.map((item) => (
                    <div key={item.id} className="bg-white border-l-4 border-[#0066FF] p-4 rounded-r-lg shadow-sm flex items-center justify-between" data-testid={`cal-item-${item.id}`}>
                      <div>
                        <p className="font-medium">{item.title}</p>
                        <p className="text-xs text-gray-400">{item.content_type} &bull; {item.scheduled_date ? new Date(item.scheduled_date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</p>
                      </div>
                      <span className={`px-2.5 py-0.5 text-xs font-bold rounded-full ${STATUS_COLORS[item.status] || STATUS_COLORS.draft}`}>{item.status?.toUpperCase()}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
